import { createWebAnalytics, WebAnalyticsModule } from '@agoda/analytics-data-acquisition';
import { ElementEventIds, EventIds } from '@agoda/analytics-messages-definition-activities';
import { getBootstrapConfig, getWebAnalytics } from 'core/window/windowService';
import { ActivitiesMessagingClient } from 'core/messaging/ActivitiesMessagingClient';
import { getPageEventIds } from 'core/messaging/analytics/pages/pageEventsMapping';
import { ActivitiesWebAppDataContextFields, WebAnalyticsBinder, WebAnalyticsContextFieldsMap } from 'core/messaging/analytics/ActivitiesAppDataContext';
import { logWarn } from 'core-framework/util/LogMessage';
import { getStoreState } from 'store/store';

type EventPayloadType = EventIds & Partial<ActivitiesWebAppDataContextFields>;

export const setupWebAnalytics = (binder: WebAnalyticsBinder, config = getBootstrapConfig()): WebAnalyticsModule => {
    const webAnalyticsModule = createWebAnalytics(ActivitiesMessagingClient, {
        webServerName: config.pageConfig && config.pageConfig.machineName,
    });

    binder(webAnalyticsModule, config);
    return webAnalyticsModule;
};

const getBootstrapConfigFromStoreOrDefault = () => {
    /**
     * We must use the BootstrapConfig from store.
     * Explanation:
     *     1. We do not update the pageTypeId in the BootstrapConfig stored in the window object.
     *     2. Please check for pageTypeId updating logic in `extraReducers` section of bootstrap slice.
     */
    const state = getStoreState();
    if (state?.bootstrap?.config) {
        return state.bootstrap.config;
    }
    return getBootstrapConfig();
};

/**
 * <b>ALERT!!</b><br/>
 * Don't use this function directly. Use <b>`trackElementEvent`</b> or <b>`trackPageVisitEvent`</b> instead. <br/>
 * This export is for testing purpose and special case like header only.
 */
export const track = (eventPayload: EventPayloadType) => {
    const config = getBootstrapConfigFromStoreOrDefault();
    const pageConfig = config.pageConfig;

    const configFields: WebAnalyticsContextFieldsMap<Partial<ActivitiesWebAppDataContextFields>> = {
        analytics_session_id: config.analyticsSessionId,
        currency: config.currency.code as ActivitiesWebAppDataContextFields['currency'],
        language: config.language.cultureCode as ActivitiesWebAppDataContextFields['language'],
        member_id: config.isLoggedIn ? config.memberId : undefined,
        session_id: config.sessionId,
        user_id: pageConfig.userId,
        channel_id: pageConfig.cid,
        whitelabelid: pageConfig.whitelabelid,
    };
    const analyticsPayload = { ...configFields, ...eventPayload };

    const webAnalytics = getWebAnalytics();
    if (webAnalytics) {
        try {
            webAnalytics.track<EventIds>(analyticsPayload);
        } catch (e) {
            // We must catch the error otherwise the component might fail to render when we fail to connect to analytics client.
            logWarn(`Error sending webAnalytics. Message ${(e as Error)?.message}`, pageConfig.pageTypeId, { action_type: analyticsPayload.action_type });
        }
    }
    return true;
};

/**
 * We initialize the common Page Context fields in the in pageContext binders for analytics pages.<br />
 * In case any field needs to be added to all events, please add it to the pageContext binder for target page.
 * Example: src/core/messaging/analytics/pages/search.ts, src/core/messaging/analytics/pages/detail.ts
 */
export const trackElementEvent = <T>(elementEventIds: ElementEventIds, eventFields: T, appContextFields?: Partial<ActivitiesWebAppDataContextFields>) => {
    const eventPayload = { ...elementEventIds, ...eventFields, ...appContextFields };

    return track(eventPayload);
};

/**
 * We initialize the common Page Context fields in the in pageContext binders for analytics pages.<br />
 * In case any field needs to be added to all events, please add it to the pageContext binder for target page.
 * Example: src/core/messaging/analytics/pages/search.ts, src/core/messaging/analytics/pages/detail.ts
 */
export const trackPageVisitEvent = <T>(eventFields: T, appContextFields?: Partial<ActivitiesWebAppDataContextFields>) => {
    const pageTypeId = getStoreState().bootstrap.config.pageConfig.pageTypeId;
    const pageEventId = getPageEventIds(pageTypeId);
    const eventPayload = { ...pageEventId, ...eventFields, ...appContextFields };

    return track(eventPayload);
};
